import { useMemo } from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { money } from "@/lib/products";

const SHADE_HEX = {
  "Mocha Glaze": "#5A3827", "Bare Honey": "#C99A7E", "Petal Silk": "#E3A3A9", "Berry Velvet": "#7E3446",
  "Honey Dew": "#DFB087", Rosewood: "#B86F6B", "Crystal Halo": "#D9C4BC", "Everyday Nudes": "#A87664", "Rose Romance": "#D88B94",
};

const weekStart = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return x;
};

export function useSalesData(orders) {
  return useMemo(() => {
    const paid = orders.filter((o) => o.payment_status === "paid");
    const now = weekStart(new Date());
    const weeks = Array.from({ length: 8 }, (_, i) => {
      const s = new Date(now);
      s.setDate(s.getDate() - (7 - i) * 7);
      return { start: s.getTime(), label: s.toLocaleDateString(undefined, { month: "short", day: "numeric" }), revenue: 0 };
    });
    const shades = {};
    paid.forEach((o) => {
      const w = weeks.find((x) => x.start === weekStart(o.created_at).getTime());
      if (w) w.revenue = Math.round((w.revenue + (o.amount ?? 0)) * 100) / 100;
      (o.items ?? []).forEach((i) => {
        const k = `${i.shade}`;
        shades[k] = shades[k] || { shade: i.shade, product: i.name, units: 0 };
        shades[k].units += i.quantity ?? 0;
      });
    });
    const top = Object.values(shades).sort((a, b) => b.units - a.units).slice(0, 6);
    return { weeks, top };
  }, [orders]);
}

const Panel = ({ title, sub, children, testid }) => (
  <div className="rounded-[28px] bg-[#FAF7F2] border border-[#E6D7CC] p-6" data-testid={testid}>
    <p className="overline text-[#6E504A]">{sub}</p>
    <p className="font-display text-2xl mt-2">{title}</p>
    <div className="mt-6">{children}</div>
  </div>
);

const tip = { contentStyle: { background: "#281715", border: "none", borderRadius: 14, color: "#FAF7F2" }, itemStyle: { color: "#FAF7F2" }, labelStyle: { color: "#EBB1B6" } };

export function SalesCharts({ orders }) {
  const { weeks, top } = useSalesData(orders);
  const max = Math.max(1, ...top.map((t) => t.units));
  return (
    <div className="grid lg:grid-cols-12 gap-4 mt-4">
      <div className="lg:col-span-7">
        <Panel title="Weekly revenue" sub="Last 8 weeks · paid orders" testid="chart-weekly-revenue">
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={weeks} margin={{ left: -10, right: 4 }}>
                <CartesianGrid vertical={false} stroke="#E6D7CC" />
                <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#6E504A" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: "#6E504A" }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v}`} />
                <Tooltip {...tip} cursor={{ fill: "#F9ECE8" }} formatter={(v) => [money(v), "Revenue"]} />
                <Bar dataKey="revenue" fill="#4A2E2B" radius={[10, 10, 4, 4]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      </div>
      <div className="lg:col-span-5">
        <Panel title="Best-selling shades" sub="Units sold · all time" testid="chart-top-shades">
          {top.length === 0 ? (
            <p className="text-sm text-[#6E504A] h-56 grid place-items-center" data-testid="top-shades-empty">No sales yet — your first shades will appear here.</p>
          ) : (
            <ul className="space-y-3 min-h-56">
              {top.map((t) => (
                <li key={t.shade} data-testid={`top-shade-${t.shade.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="flex justify-between text-sm"><span>{t.shade} <span className="text-[#6E504A]">· {t.product}</span></span><span className="font-medium">{t.units}</span></div>
                  <div className="mt-1.5 h-2.5 rounded-full bg-[#F4EFE6] overflow-hidden">
                    <div className="h-full rounded-full transition-[width] duration-700" style={{ width: `${(t.units / max) * 100}%`, background: SHADE_HEX[t.shade] ?? "#D88B94" }} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </div>
  );
}
